import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Math as MathFormula } from "@/components/Math";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function GibbsFreeEnergyCalculator() {
  const [enthalpy, setEnthalpy] = useState("");
  const [entropy, setEntropy] = useState("");
  const [temperature, setTemperature] = useState("");
  const [result, setResult] = useState<{ deltaG: number; spontaneity: string } | null>(null);

  const calculate = () => {
    const h = parseFloat(enthalpy);
    const s = parseFloat(entropy);
    const t = parseFloat(temperature);

    if (isNaN(h) || isNaN(s) || isNaN(t) || t < 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid numbers. Temperature must be positive.",
        variant: "destructive",
      });
      return;
    }

    // ΔG = ΔH - TΔS (convert entropy from J to kJ)
    const deltaG = h - (t * s / 1000);
    const spontaneity = deltaG < 0 ? "Spontaneous" : deltaG > 0 ? "Non-spontaneous" : "At Equilibrium";

    setResult({ deltaG, spontaneity });

    toast({
      title: "Calculation Complete",
      description: `ΔG = ${deltaG.toFixed(2)} kJ`,
    });
  };

  const reset = () => {
    setEnthalpy("");
    setEntropy("");
    setTemperature("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Gibbs Free Energy Calculator" 
      calculatorPath="/calculator/gibbs-free-energy"
      category="chemistry"
      subcategory="Thermochemistry"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Gibbs Free Energy Calculator</CardTitle>
            <CardDescription>
              Calculate ΔG and predict spontaneity
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="enthalpy">ΔH (kJ/mol)</Label>
                <Input
                  id="enthalpy"
                  type="number"
                  step="any"
                  value={enthalpy}
                  onChange={(e) => setEnthalpy(e.target.value)}
                  placeholder="Enthalpy change"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="entropy">ΔS (J/mol·K)</Label>
                <Input
                  id="entropy"
                  type="number"
                  step="any"
                  value={entropy}
                  onChange={(e) => setEntropy(e.target.value)}
                  placeholder="Entropy change"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="temperature">Temperature (K)</Label>
                <Input
                  id="temperature"
                  type="number"
                  step="any"
                  value={temperature}
                  onChange={(e) => setTemperature(e.target.value)}
                  placeholder="Temperature in Kelvin"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result && (
              <>
                <div className="space-y-4">
                  <Card className="bg-muted/50">
                    <CardHeader>
                      <CardTitle className="text-lg">Gibbs Free Energy (ΔG)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold">{result.deltaG.toFixed(2)} kJ/mol</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-muted/50">
                    <CardHeader>
                      <CardTitle className="text-lg">Spontaneity</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xl">{result.spontaneity}</p>
                    </CardContent>
                  </Card>
                </div>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["gibbs-free-energy"]} trigger={result.deltaG} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Formula</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <MathFormula block>{String.raw`\Delta G = \Delta H - T\Delta S`}</MathFormula>
                <p className="text-sm text-muted-foreground">
                  • <MathFormula>{String.raw`\Delta G < 0`}</MathFormula>: Spontaneous<br />
                  • <MathFormula>{String.raw`\Delta G > 0`}</MathFormula>: Non-spontaneous<br />
                  • <MathFormula>{String.raw`\Delta G = 0`}</MathFormula>: At equilibrium
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
